import {
  Box,
  Typography,
  CircularProgress,
  useTheme,
  Avatar,
  IconButton,
} from "@mui/material";
import { useProgress } from "context/ProgressProvider";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { LoanService } from "services/LoanService";
import {
  AppPrimaryButton,
  AppPrimaryButtonOutlined,
} from "shared/components/AppComponents";
import {
  KYCStatusCode,
  KYCStatusColor,
  KYCStatusLabel,
  KYCStatusText,
} from "shared/helpers/Constants";
import Toast from "shared/helpers/Toast";
import Utils from "shared/helpers/Utils";
import { ProfileActions } from "state/profile/Action";
import { useNavigate } from "react-router-dom";
import { StakeholderService } from "services/StakeholderService";
import { getRoleLabel } from "./Directors";
import IdentityVerificationFailedDialog from "./dialog/IdentityVerificationFailedDialog";

export interface KYCVerificationStatusProps {
  isKYCCompleted: number;
  iVerifyLink: string;
  email: string;
}

const IdentityVerification = () => {
  const { handleCompleteAndNext, currentProfile, isBroker } = useProgress();
  const dispatch = useDispatch();
  const [loadingKYCstatus, setLoadingKYCstatus] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [kycInfo, setKYCInfo] = useState({} as KYCVerificationStatusProps);
  const [isInitiating, setIsInitiating] = useState(false);
  const [showVerificationFailedDialog, setShowVerificationFailedDialog] =
    useState(false);

  const theme = useTheme();
  const navigate = useNavigate();

  // API Calls
  const loadKYCVerificationStatus = (
    callback: (res: KYCVerificationStatusProps) => void = () => {},
  ) => {
    setLoadingKYCstatus(true);
    LoanService.getKYCVerificationStatus(
      currentProfile.loadId,
      currentProfile.kycId,
    )
      .then((response: any) => {
        const data = response.data.data;
        if (data) {
          dispatch(
            ProfileActions.setRndProfile({
              identityVerificationStatus: data.isKYCCompleted,
            }),
          );
          setKYCInfo(data);
          if (data.isKYCCompleted === KYCStatusCode.VERIFIED) {
            // navigate("/sbl/final-solicitor");
          } else if (data.isKYCCompleted === KYCStatusCode.FAILED) {
            setShowVerificationFailedDialog(true);
          }
          callback(data);
        }
      })
      .catch((error: any) => {
        Utils.handleErrorResponse(error);
      })
      .finally(() => {
        setLoadingKYCstatus(false);
      });
  };

  const handleGetAllStakeholders = () => {
    StakeholderService.getAllStakeholders(currentProfile.loadId)
      .then((response: any) => {
        console.log(response);
        let data = response.data.data.stakeholders;

        dispatch(
          ProfileActions.setRndProfile({
            stakeholders: data,
          }),
        );
      })
      .catch((error: any) => {
        Utils.handleErrorResponse(error);
      })
      .finally(() => {});
  };

  const resendKYC = () => {
    setIsResending(true);
    LoanService.resendKYC({
      email: currentProfile.email,
      loanId: currentProfile.loadId,
    })
      .then((response: any) => {
        console.log(response);
        Utils.handleSuccessResponse(response);
      })
      .catch((error: any) => {
        Utils.handleErrorResponse(error);
      })
      .finally(() => {
        setIsResending(false);
      });
  };

  const initiateKYC = () => {
    setIsInitiating(true);
    LoanService.initiateKYC({
      email: currentProfile.email,
      loanId: currentProfile.loadId,
      kycId: currentProfile.kycId,
    })
      .then((response: any) => {
        const data = response.data.data;
        if (data) {
          dispatch(
            ProfileActions.setRndProfile({
              identityVerificationStatus: data.isKYCCompleted,
            }),
          );
        }
        setShowVerificationFailedDialog(false);
        loadKYCVerificationStatus();
      })
      .catch((error: any) => {
        Utils.handleErrorResponse(error);
      })
      .finally(() => {
        setIsInitiating(false);
      });
  };

  // UI Actions
  const actionOnContinue = () => {
    loadKYCVerificationStatus((data: any) => {
      console.log("data", data);
      if (data.isKYCCompleted === KYCStatusCode.VERIFIED) {
        handleCompleteAndNext();
      } else if (data.isKYCCompleted === KYCStatusCode.FAILED) {
        setShowVerificationFailedDialog(true);
      } else {
        Toast.showInfoMessage(
          "Please complete identity verification before proceeding",
        );
      }
    });
  };

  const actionOnClickHere = () => {
    const { iVerifyLink, isKYCCompleted } = kycInfo;
    if (isKYCCompleted === KYCStatusCode.VERIFIED) {
      Toast.showSuccessMessage(
        isBroker
          ? "Your customer identity verification is already completed. Please proceed to the next step."
          : "Your identity verification is already completed. Please proceed to the next step.",
      );
    } else if (isKYCCompleted === KYCStatusCode.FAILED) {
      setShowVerificationFailedDialog(true);
    } else {
      if (iVerifyLink) {
        window.open(iVerifyLink, "_blank");
      }
    }
  };

  const actionOnResendKYC = () => {
    const { isKYCCompleted } = kycInfo;
    if (isKYCCompleted === KYCStatusCode.VERIFIED) {
      Toast.showSuccessMessage(
        isBroker
          ? "Your customer identity verification is already completed. Please proceed to the next step."
          : "Your identity verification is already completed. Please proceed to the next step.",
      );
    } else if (isKYCCompleted === KYCStatusCode.FAILED) {
      setShowVerificationFailedDialog(true);
    } else {
      resendKYC();
    }
  };

  const onCloseForm = (): void => {
    setShowVerificationFailedDialog(false);
    // loadKYCVerificationStatus();
  };

  // Hooks
  useEffect(() => {
    loadKYCVerificationStatus();
    handleGetAllStakeholders();
  }, []);

  console.log("kycInfo", kycInfo);

  // UI Elements
  return (
    <>
      <Box mt={4}>
        <Typography variant="h2">Identity verification</Typography>

        {currentProfile.identityVerificationStatus !== 2 ? (
          <>
            <Typography variant="body1" className="mt-2 f14">
              {isBroker ? (
                <>
                  Identity verification link has been sent to the email. Please
                  complete the verification to continue.
                </>
              ) : (
                <>
                  Identity verification link has been sent to your email. Please
                  complete the verification to continue.
                </>
              )}
            </Typography>

            {loadingKYCstatus && (
              <Box className="empty-content">
                <CircularProgress />
              </Box>
            )}

            {!loadingKYCstatus && (
              <>
                <Typography
                  variant="body1"
                  className="pt-5 text-primary cursor-pointer text-title f16"
                  onClick={actionOnClickHere}
                >
                  Click here to verify
                </Typography>

                <Box className="flex pt-5">
                  <Typography variant="body1" className="f14">
                    {isBroker ? (
                      <>
                        If not received the email, click the "Resend Link"
                        button to receive it again
                      </>
                    ) : (
                      <>
                        If you have not received the email, click the "Resend
                        Link" button to receive it again
                      </>
                    )}
                  </Typography>
                </Box>

                <Box className="flex pt-5">
                  <AppPrimaryButton
                    title="Continue"
                    isLoading={false}
                    disabled={isResending}
                    onClick={actionOnContinue}
                  />

                  <AppPrimaryButtonOutlined
                    title="Resend Link"
                    isLoading={isResending}
                    loadingText="RESENDING"
                    disabled={
                      isResending ||
                      currentProfile.identityVerificationStatus ===
                        KYCStatusCode.FAILED
                    }
                    onClick={actionOnResendKYC}
                    className="ml-5"
                  />
                </Box>

                {/* List Directors */}
                {/* <Box sx={{ borderTop: "1px solid #e7e7e7", marginTop: "2rem" }}>
              {[KYCStatusCode.SENT, KYCStatusCode.VERIFIED].map((status) => {
                const filtered = currentProfile.stakeholders?.filter(
                  (s) => s.kycStatus === status
                );

                return (
                  <Box key={status} mt={4}>
                    <Typography
                      variant="h3"
                      fontWeight={500}
                      sx={{
                        mb: 2,
                        textTransform: "capitalize",
                      }}
                    >
                      {KYCStatusLabel[status]}:
                    </Typography>

                    {filtered?.length ? (
                      filtered.map((data, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            width: "380px",
                            border: "1px solid #E5E5E5",
                            borderRadius: "8px",
                            padding: "5px 16px",
                            justifyContent: "space-between",
                            mt: 2,
                            [theme.breakpoints.down("md")]: {
                              width: "unset",
                            },
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: "12px",
                            }}
                          >
                            <Avatar
                              sx={{
                                background:
                                  "linear-gradient(257.91deg, #0575E6 -9.32%, #021B79 78.43%, #000000 177.87%)",
                              }}
                            >
                              {data.firstName?.[0]}
                              {data.lastName?.[0]}
                            </Avatar>
                            <Box>
                              <Typography variant="h3" fontWeight={400}>
                                {data.firstName} {data.lastName}
                              </Typography>
                              <Typography
                                variant="h4"
                                fontWeight={400}
                                sx={{ marginTop: "-5px" }}
                              >
                                {getRoleLabel(data.role)}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      ))
                    ) : (
                      <></>
                    )}
                  </Box>
                );
              })}
            </Box> */}
              </>
            )}
          </>
        ) : (
          <>
            <Typography variant="body1" className="mt-2 f14">
              Identity verification is completed!
            </Typography>
            <Box sx={{ marginTop: "1rem" }}>
              <AppPrimaryButton
                title="Continue"
                isLoading={false}
                disabled={isResending}
                onClick={() => handleCompleteAndNext()}
              />
            </Box>
          </>
        )}
      </Box>
      {showVerificationFailedDialog && (
        <IdentityVerificationFailedDialog
          showConfirm={showVerificationFailedDialog}
          onCloseForm={onCloseForm}
          actionOnInitiateKYC={initiateKYC}
          isInitiating={isInitiating}
        />
      )}
    </>
  );
};

export default IdentityVerification;
